import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  Send, Users, User, Briefcase, Home, Newspaper, Calendar, Tag,
  Recycle, MessageSquare, Wrench, Building2, MapPin, X, Search, Navigation, Car, ShoppingBag, Mail, Gamepad2, Trophy,
  Coffee, Moon,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// ── Audience ─────────────────────────────────────────────────────────────────
const AUDIENCES = [
  { value: "all", label: "Everyone", icon: Users },
  { value: "self", label: "My device", icon: User },
  { value: "residents", label: "Residents", icon: User },
  { value: "trades", label: "Tradespeople", icon: Briefcase },
  { value: "drivers", label: "Drivers", icon: Car },
];

// ── Destination sections ──────────────────────────────────────────────────────
type SectionKey =
  | "none" | "home" | "news" | "events" | "deals"
  | "jobs" | "freecycle" | "community" | "trade" | "directory" | "order"
  | "pac_game" | "pac_royale_game";

interface SectionDef {
  key: SectionKey;
  label: string;
  icon: React.FC<{ size?: number }>;
  tabPath: string | null;
  itemEndpoint: string | null;
  itemDetailPath: ((id: number) => string) | null;
  itemLabelKey: string;
}

const SECTIONS: SectionDef[] = [
  { key: "home",      label: "Home",          icon: Home,         tabPath: "/(tabs)",            itemEndpoint: null,            itemDetailPath: null,                    itemLabelKey: "title" },
  { key: "news",      label: "News",          icon: Newspaper,    tabPath: "/(tabs)/news",       itemEndpoint: "/api/news",      itemDetailPath: (id) => `/news/${id}`,   itemLabelKey: "title" },
  { key: "events",    label: "Events",        icon: Calendar,     tabPath: "/(tabs)/events",     itemEndpoint: "/api/events",    itemDetailPath: (id) => `/event/${id}`,  itemLabelKey: "title" },
  { key: "deals",     label: "Deals",         icon: Tag,          tabPath: "/(tabs)/deals",      itemEndpoint: "/api/deals",     itemDetailPath: (id) => `/deal/${id}`,   itemLabelKey: "title" },
  { key: "jobs",      label: "Jobs",          icon: MapPin,       tabPath: "/(tabs)/trade",      itemEndpoint: "/api/jobs",      itemDetailPath: (id) => `/jobs/${id}`,   itemLabelKey: "title" },
  { key: "order",     label: "Food Ordering", icon: ShoppingBag,  tabPath: "/(tabs)/order",      itemEndpoint: null,             itemDetailPath: null,                    itemLabelKey: "title" },
  { key: "freecycle", label: "Market",    icon: Recycle,      tabPath: "/(tabs)/freecycle",  itemEndpoint: null,            itemDetailPath: null,                    itemLabelKey: "title" },
  { key: "community", label: "Community",     icon: MessageSquare,tabPath: "/(tabs)/community",  itemEndpoint: null,            itemDetailPath: null,                    itemLabelKey: "title" },
  { key: "trade",     label: "Trade / Leads", icon: Wrench,       tabPath: "/(tabs)/trade",      itemEndpoint: null,            itemDetailPath: null,                    itemLabelKey: "title" },
  { key: "directory",      label: "Directory",    icon: Building2, tabPath: "/(tabs)/directory",  itemEndpoint: "/api/businesses", itemDetailPath: (id) => `/biz/${id}`, itemLabelKey: "name" },
  { key: "pac_game",       label: "🕹️ Pac-Man",  icon: Gamepad2,  tabPath: "pac",                itemEndpoint: null,              itemDetailPath: null,                 itemLabelKey: "title" },
  { key: "pac_royale_game",label: "👑 Pac Royale",icon: Trophy,    tabPath: "pac_royale",         itemEndpoint: null,              itemDetailPath: null,                 itemLabelKey: "title" },
];

interface ItemOption { id: number; label: string }

function toScreenPath(section: SectionKey, item: ItemOption | null): string | null {
  const def = SECTIONS.find((s) => s.key === section);
  if (!def || !def.tabPath) return null;
  if (item && def.itemDetailPath) return def.itemDetailPath(item.id);
  return def.tabPath;
}

// ── Item picker ───────────────────────────────────────────────────────────────
function ItemPicker({
  section,
  selected,
  onSelect,
  onClear,
}: {
  section: SectionDef;
  selected: ItemOption | null;
  onSelect: (item: ItemOption) => void;
  onClear: () => void;
}) {
  const [items, setItems] = useState<ItemOption[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!section.itemEndpoint) return;
    setLoading(true);
    fetch(section.itemEndpoint, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data: unknown) => {
        const rows = Array.isArray(data)
          ? data
          : ((data as { items?: unknown[] } | null)?.items ?? []);
        setItems(
          (rows as Record<string, unknown>[]).map((row) => ({
            id: row["id"] as number,
            label: (row[section.itemLabelKey] as string) ?? `#${row["id"] as number}`,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [section]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = items.filter((i) =>
    i.label.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 8);

  if (selected) {
    return (
      <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl px-3 py-2">
        <span className="text-sm font-medium text-primary flex-1">{selected.label}</span>
        <button
          onClick={() => { onClear(); setQuery(""); }}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 focus-within:border-primary transition-colors">
        <Search size={14} className="text-slate-400 shrink-0" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? "Loading…" : `Search ${section.label.toLowerCase()}…`}
          className="flex-1 text-sm outline-none bg-transparent"
        />
        {query && (
          <button onClick={() => { setQuery(""); setOpen(false); }} className="text-slate-400 hover:text-slate-600">
            <X size={12} />
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {filtered.map((item) => (
            <button
              key={item.id}
              onClick={() => { onSelect(item); setOpen(false); setQuery(""); }}
              className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 truncate"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {open && !loading && filtered.length === 0 && query.length > 1 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-sm text-slate-400">
          No results for "{query}"
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminNotifications() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<"all" | "self" | "residents" | "trades" | "drivers">("all");
  /** Devices reachable per audience. Counts phones, not people — see below. */
  const [devices, setDevices] = useState<{ all: number; anonymous: number; residents: number; trades: number } | null>(null);
  const [sending, setSending] = useState(false);

  const [section, setSection] = useState<SectionKey>("none");
  const [selectedItem, setSelectedItem] = useState<ItemOption | null>(null);

  const [campaign, setCampaign] = useState<"breakfast" | "evening">("breakfast");
  const [emailDryRun, setEmailDryRun] = useState<{ total: number; stores: number; storeNames: string[]; sample: string[] } | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<{ sent: number; failed: number } | null>(null);

  const handleEmailDryRun = async () => {
    setEmailSending(true); setEmailDryRun(null); setEmailResult(null);
    try {
      const res = await fetch("/api/admin/marketing-email/campaign", { method: "POST", headers: authHeaders(), body: JSON.stringify({ campaign, dryRun: true }) });
      const d = await res.json() as { total?: number; stores?: number; storeNames?: string[]; sample?: string[]; error?: string };
      if (!res.ok) { toast({ title: d.error ?? "Failed", variant: "destructive" }); return; }
      setEmailDryRun({ total: d.total ?? 0, stores: d.stores ?? 0, storeNames: d.storeNames ?? [], sample: d.sample ?? [] });
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setEmailSending(false); }
  };

  const handleEmailSend = async () => {
    if (!confirm(`Send ${campaign === "breakfast" ? "Breakfast & Lunch" : "Evening Feast"} email to ${emailDryRun?.total ?? "all"} users? This cannot be undone.`)) return;
    setEmailSending(true); setEmailResult(null);
    try {
      const res = await fetch("/api/admin/marketing-email/campaign", { method: "POST", headers: authHeaders(), body: JSON.stringify({ campaign, dryRun: false }) });
      const d = await res.json() as { sent?: number; failed?: number; error?: string };
      if (!res.ok) { toast({ title: d.error ?? "Failed", variant: "destructive" }); return; }
      setEmailResult({ sent: d.sent ?? 0, failed: d.failed ?? 0 });
      setEmailDryRun(null);
      toast({ title: `Sent to ${d.sent ?? 0} users` });
    } catch { toast({ title: "Network error", variant: "destructive" }); }
    finally { setEmailSending(false); }
  };

  const screen = section === "none" ? null : toScreenPath(section, selectedItem);
  const sectionDef = SECTIONS.find((s) => s.key === section) ?? null;

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/push-status", { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json() as {
          devices?: { all: number; anonymous: number; residents: number; trades: number };
        };
        setDevices(data.devices ?? null);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const handleSectionChange = (key: SectionKey) => {
    setSection(key);
    setSelectedItem(null);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast({ title: "Please fill in title and message", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        body: body.trim(),
        audience,
      };
      if (screen) payload.screen = screen;

      const res = await fetch("/api/admin/push-notifications/send", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const result = await res.json() as { sent?: number; failed?: number; totalTargeted?: number; error?: string };
      if (!res.ok) throw new Error(result.error ?? "Failed");
      const sent = result.sent ?? 0;
      const failed = result.failed ?? 0;
      toast({
        title: sent > 0 ? "Notification sent!" : "No notifications sent",
        description: `${sent} accepted for delivery${failed > 0 ? `, ${failed} failed` : ""}.`,
        variant: sent > 0 ? "default" : "destructive",
      });
      setTitle("");
      setBody("");
      setSection("none");
      setSelectedItem(null);
    } catch {
      toast({ title: "Failed to send", description: "Please try again.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout current="/admin/push">
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">Push Notifications</h1>
          <p className="text-sm text-slate-500">
            Send push notifications to app users.
            {/* Reach is measured in DEVICES, because that is what actually gets
                notified. The old line counted registered users, which
                understated an Everyone broadcast about threefold: most installs
                never sign in, and those phones are reachable but cannot be
                filtered by role or town. */}
            {devices !== null ? (
              <span className="ml-2 font-semibold text-slate-700">
                {audience === "all"
                  ? `Reaches ${devices.all} device${devices.all !== 1 ? "s" : ""}`
                  : audience === "residents"
                    ? `Reaches ${devices.residents} device${devices.residents !== 1 ? "s" : ""}`
                    : audience === "trades"
                      ? `Reaches ${devices.trades} device${devices.trades !== 1 ? "s" : ""}`
                      : audience === "self"
                        ? "Reaches your device only"
                        : "Reaches approved drivers only"}
              </span>
            ) : null}
          </p>
          {devices !== null && audience !== "all" && devices.anonymous > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
              {devices.anonymous} more device{devices.anonymous !== 1 ? "s" : ""} have the app but have never
              signed in. They can only be reached with <strong>Everyone</strong>.
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 max-w-xl">

          {/* Audience */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Audience</label>
            <div className="flex gap-2 flex-wrap">
              {AUDIENCES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setAudience(value as typeof audience)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-colors"
                  style={{
                    background: audience === value ? "#1e3a5f" : "var(--color-card)",
                    borderColor: audience === value ? "#1e3a5f" : "#e2e8f0",
                    color: audience === value ? "var(--color-card)" : "#64748b",
                  }}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Title *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Ayrshire Hub Update"
              maxLength={100}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Message *</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What do you want to tell your community?"
              rows={4}
              maxLength={500}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary resize-none transition-colors leading-relaxed"
            />
            <p className="text-xs text-slate-400 text-right mt-1">{body.length}/500</p>
          </div>

          {/* Destination */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Destination <span className="normal-case font-normal text-slate-400">(optional — where tapping opens)</span>
            </label>

            {/* Section grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
              {/* None pill */}
              <button
                onClick={() => handleSectionChange("none")}
                className="col-span-2 sm:col-span-3 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border transition-colors"
                style={{
                  background: section === "none" ? "#1e3a5f" : "var(--color-card)",
                  borderColor: section === "none" ? "#1e3a5f" : "#e2e8f0",
                  color: section === "none" ? "var(--color-card)" : "#94a3b8",
                }}
              >
                No destination (notification only)
              </button>

              {SECTIONS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => handleSectionChange(key)}
                  className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-xs font-medium border transition-colors"
                  style={{
                    background: section === key ? "#1e3a5f" : "var(--color-card)",
                    borderColor: section === key ? "#1e3a5f" : "#e2e8f0",
                    color: section === key ? "var(--color-card)" : "#64748b",
                  }}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </div>

            {/* Item picker — only for sections with sub-items */}
            {section !== "none" && sectionDef?.itemEndpoint && (
              <div className="mb-2">
                <p className="text-xs text-slate-400 mb-1.5">
                  Specific item <span className="text-slate-300">(optional — leave blank to open the {sectionDef.label} section)</span>
                </p>
                <ItemPicker
                  section={sectionDef}
                  selected={selectedItem}
                  onSelect={setSelectedItem}
                  onClear={() => setSelectedItem(null)}
                />
              </div>
            )}

            {/* Screen path preview */}
            {screen && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mt-2">
                <Navigation size={13} className="text-emerald-600 shrink-0" />
                <span className="text-xs font-mono text-emerald-700">{screen}</span>
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 font-medium">
              ⚠️ This sends an immediate push notification to users with the app installed and notifications enabled
              {audience !== "all" ? ` (${AUDIENCES.find((a) => a.value === audience)?.label} only)` : ""}. Use sparingly.
            </p>
          </div>

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={!title.trim() || !body.trim() || sending}
            className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-colors"
          >
            {sending ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Send size={16} /> Send Notification</>
            )}
          </button>
        </div>

        {/* Marketing email blast */}
        <div>
          <h2 className="text-xl font-bold text-primary mb-1">Marketing Email Blast</h2>
          <p className="text-sm text-slate-500 mb-4">Send a branded marketing email to all registered users who haven't unsubscribed. Stores shown are automatically filtered to those open today.</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 max-w-2xl">

          {/* Campaign picker */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Campaign Template</label>
            <div className="flex gap-3">
              <button
                onClick={() => { setCampaign("breakfast"); setEmailDryRun(null); setEmailResult(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-colors"
                style={{ background: campaign === "breakfast" ? "#1e3a5f" : "#fff", borderColor: campaign === "breakfast" ? "#1e3a5f" : "#e2e8f0", color: campaign === "breakfast" ? "#fff" : "#64748b" }}
              >
                <Coffee size={15} /> Breakfast &amp; Lunch
              </button>
              <button
                onClick={() => { setCampaign("evening"); setEmailDryRun(null); setEmailResult(null); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-colors"
                style={{ background: campaign === "evening" ? "#1e3a5f" : "#fff", borderColor: campaign === "evening" ? "#1e3a5f" : "#e2e8f0", color: campaign === "evening" ? "#fff" : "#64748b" }}
              >
                <Moon size={15} /> Evening Feast
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              {campaign === "breakfast"
                ? "Sends the daytime email — cafés, rolls, bakeries open today. Price: Square Sausage Roll £2.25 vs £2.99 on JE."
                : "Sends the evening email — curries, kebabs, pizza open tonight. Price: Chicken Tikka Chasni £9.99 vs £12.95 on JE."}
            </p>
          </div>

          {emailResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
              ✓ Sent to <strong>{emailResult.sent}</strong> users.{emailResult.failed > 0 ? ` ${emailResult.failed} failed.` : ""}
            </div>
          )}

          {emailDryRun && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 space-y-2">
              <p className="font-semibold">Dry run — {campaign === "breakfast" ? "Breakfast & Lunch" : "Evening Feast"}:</p>
              <p>• <strong>{emailDryRun.total}</strong> users will receive the email</p>
              <p>• <strong>{emailDryRun.stores}</strong> stores open today will be featured</p>
              {emailDryRun.storeNames.length > 0 && <p className="text-xs text-blue-600">Stores: {emailDryRun.storeNames.join(", ")}</p>}
              {emailDryRun.sample.length > 0 && <p className="text-xs text-blue-500">Sample recipients: {emailDryRun.sample.join(", ")}</p>}
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 font-medium">⚠️ Run a dry run first to confirm which stores are open today before sending for real.</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => void handleEmailDryRun()}
              disabled={emailSending}
              className="flex-1 flex items-center justify-center gap-2 border border-primary text-primary disabled:opacity-50 font-semibold py-3 rounded-xl transition-colors hover:bg-primary/5"
            >
              {emailSending ? <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <><Mail size={15} /> Dry Run</>}
            </button>
            <button
              onClick={() => void handleEmailSend()}
              disabled={emailSending || !emailDryRun}
              className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors"
            >
              {emailSending ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Send size={15} /> Send Email</>}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
